import { useEffect, useRef, useState } from 'react';

/**
 * Similar to a `debounceCallback` type method, but also provides the 'live' value
 * for immediate feedback. In this case useful for a UI element (a slider) to look
 * immediately responsive, while the client callback is invoked in a debounced manner.
 *
 * @returns
 * A tuple `[liveValue, setLiveValue]` where:
 *
 * - `liveValue` is the immediate value used for rendering UI.
 * - `setLiveValue` updates the live value immediately and schedules a
 *   debounced call to `onChange`.
 *
 * @example
 * ```tsx
 * const [liveValue, setLiveValue] = useDebounce(value, setValue, 100);
 *
 * return (
 *   <input
 *     type="range"
 *     value={liveValue}
 *     onChange={(e) => setLiveValue(Number(e.target.value))}
 *   />
 * );
 * ```
 *
 * In this example the slider moves smoothly, but `setValue` is only called
 * after the user stops moving the slider for 100ms.
 */
function useDebounce<T>(value: T, onChange: (v: T) => void, delay: number) {
  const [liveValue, setLiveValue] = useState(value);

  // we do not want to reset the debounce when onChange is updated
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Keep local value in sync with parent
  useEffect(() => {
    setLiveValue(value);
  }, [value]);

  useEffect(() => {
    if (liveValue === value) return; // early return

    const timer = setTimeout(() => {
      onChangeRef.current(liveValue); // always calls the latest onChange
    }, delay);

    return () => clearTimeout(timer);
  }, [liveValue, delay, value]);

  return [liveValue, setLiveValue] as const;
}

export { useDebounce };
